import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { AppLayout } from '../components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, KeyRound, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getLicenseManagerDashboard,
  licenseManagerAction,
  getUserOfflineComputers,
  createOfflineComputer,
  apiClient,
} from '../services/api';
import { useUserStore, useIsAdmin } from '../store/userStore';
import type { LicenseManagerDashboard as DashboardType, LicenseManagerUser } from '../types';

const BUY_LICENSE_URL = 'https://folge.me/business-license';

function generateRandomPassword(length = 15): string {
  // Alphanumeric + symbols only (no dashes/hyphens)
  const charset =
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)];
  }
  return password;
}

const LicenseManagerPage: React.FC = () => {
  const navigate = useNavigate();
  const account = useUserStore((s) => s.account);
  const isAdmin = useIsAdmin();
  const logout = useUserStore((s) => s.logout);
  const [data, setData] = useState<DashboardType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offlineModal, setOfflineModal] = useState<{
    open: boolean;
    userId: string | null;
    user: { fullname: string; email: string } | null;
    computers: { id: number; machine_name: string; key2_signed: string; created_at: string }[];
    loading: boolean;
    newComputerName: string;
    key1: string;
    adding: boolean;
  }>({
    open: false,
    userId: null,
    user: null,
    computers: [],
    loading: false,
    newComputerName: '',
    key1: '',
    adding: false,
  });
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<{ name: string; email: string; password: string }[]>([]);
  const [csvRowStatus, setCsvRowStatus] = useState<Record<number, 'pending' | 'processing' | 'success' | 'error'>>({});
  const [csvRowMessage, setCsvRowMessage] = useState<Record<number, string>>({});

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiClient.getLicenseManagerDashboard();
      setData(result);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        logout();
        navigate('/login', { replace: true });
        return;
      }
      setError('Failed to load license manager data.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleAction = async (
    action: 'add_user' | 'create_user' | 'remove_user',
    params: { email?: string; fullname?: string; password?: string; send_email?: number }
  ) => {
    try {
      const res = await licenseManagerAction({
        action,
        ...params,
      });
      if (res.message && (res as { successCode?: string }).successCode) {
        toast.success(res.message);
      } else if (res.message && (res as { errorCode?: string }).errorCode) {
        toast.error(res.message);
        return;
      }
      await fetchDashboard();
    } catch {
      toast.error('Action failed');
    }
  };

  const openOfflineModal = async (userId: string) => {
    setOfflineModal((prev) => ({ ...prev, open: true, userId, loading: true, computers: [], user: null }));
    try {
      const result = await getUserOfflineComputers(userId);
      setOfflineModal((prev) => ({
        ...prev,
        computers: result.computers || [],
        user: result.user || null,
        loading: false,
        newComputerName: '',
        key1: '',
      }));
    } catch {
      toast.error('Failed to load offline computers');
      setOfflineModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const addOfflineComputer = async () => {
    if (!offlineModal.userId || !offlineModal.newComputerName.trim() || !offlineModal.key1.trim()) {
      toast.error('Please enter computer name and Key1');
      return;
    }
    setOfflineModal((prev) => ({ ...prev, adding: true }));
    try {
      const result = await createOfflineComputer({
        userId: offlineModal.userId,
        computerName: offlineModal.newComputerName.trim(),
        key1: offlineModal.key1.trim(),
      });
      if (result.error) {
        toast.error(result.error);
        return;
      }
      setOfflineModal((prev) => ({
        ...prev,
        computers: result.computers || prev.computers,
        adding: false,
        newComputerName: '',
        key1: '',
      }));
      if (result.newComputer) {
        toast.success(`Offline computer "${result.newComputer.machine_name}" added. Copy Key2 from the table.`);
      }
    } catch {
      toast.error('Failed to add offline computer');
      setOfflineModal((prev) => ({ ...prev, adding: false }));
    }
  };

  const parseCsv = (text: string): { name: string; email: string; password: string }[] => {
    const rows = text.split('\n');
    const users: { name: string; email: string; password: string }[] = [];
    for (const row of rows) {
      if (!row.trim()) continue;
      const cols = row.split(',').map((c) => c.trim());
      const name = cols[0] || '';
      const email = (cols[1] || '').toLowerCase();
      if (name && email) {
        users.push({ name, email, password: generateRandomPassword(15) });
      }
    }
    return users;
  };

  const onCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    setCsvPreview([]);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result));
      setCsvPreview(parsed);
      setCsvRowStatus({});
      setCsvRowMessage({});
    };
    reader.readAsText(file);
  };

  const createAllCsvUsers = async () => {
    if (csvPreview.length === 0) return;
    for (let i = 0; i < csvPreview.length; i++) {
      setCsvRowStatus((s) => ({ ...s, [i]: 'processing' }));
      setCsvRowMessage((m) => ({ ...m, [i]: '' }));
      try {
        const res = await licenseManagerAction({
          action: 'create_user',
          email: csvPreview[i].email,
          fullname: csvPreview[i].name,
          password: csvPreview[i].password,
        });
        if ((res as { successCode?: string }).successCode) {
          setCsvRowStatus((s) => ({ ...s, [i]: 'success' }));
          setCsvRowMessage((m) => ({ ...m, [i]: res.message || 'Created and assigned' }));
        } else {
          setCsvRowStatus((s) => ({ ...s, [i]: 'error' }));
          setCsvRowMessage((m) => ({ ...m, [i]: res.message || 'Error' }));
        }
      } catch {
        setCsvRowStatus((s) => ({ ...s, [i]: 'error' }));
        setCsvRowMessage((m) => ({ ...m, [i]: 'Request failed' }));
      }
    }
    await fetchDashboard();
  };

  const breadcrumbs = [{ title: 'License Manager', isCurrentPage: true }];

  if (account && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (loading && !data) {
    return (
      <AppLayout showSidebar breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (error && !data) {
    return (
      <AppLayout showSidebar breadcrumbs={breadcrumbs}>
        <div className="p-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </AppLayout>
    );
  }

  const d = data!;
  const licenseUsers: LicenseManagerUser[] = d.licenseUsers || [];
  const totalLicenses = d.licenses?.length ?? 0;
  const assignedCount = d.assignedLicensesNumber ?? 0;
  const userEmail = d.user?.email ?? '';

  return (
    <AppLayout showSidebar breadcrumbs={breadcrumbs}>
      <div className="space-y-6 p-4">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <KeyRound className="h-6 w-6" />
            Folge licenses management
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Assign and remove licenses for your users.
          </p>
        </div>

        {/* Stats */}
        <Card className="bg-muted/40">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-medium text-muted-foreground">Total available</span>
                <span className="text-2xl font-semibold tabular-nums">{totalLicenses}</span>
                <span className="text-sm text-muted-foreground">licenses</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-medium text-muted-foreground">Assigned</span>
                <span className="text-2xl font-semibold tabular-nums">{assignedCount}</span>
                <span className="text-sm text-muted-foreground">seats</span>
              </div>
              <div className="flex-1 min-w-0" />
              <a
                href={BUY_LICENSE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md text-sm font-semibold bg-amber-400 text-amber-950 border-2 border-amber-500 hover:bg-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 h-9 px-4 shadow-sm"
              >
                Buy more licenses
              </a>
            </div>
            <p className="text-sm text-muted-foreground mt-4 pt-4 border-t border-border/50">
              Unassigned seats are counted against your root account <strong className="text-foreground">{userEmail}</strong>
            </p>
          </CardContent>
        </Card>

        {(d.error || d.success) && (
          <Alert variant={d.error ? 'destructive' : 'default'} className="max-w-2xl">
            <AlertDescription>{d.error || d.success}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {/* Assign seat */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Assign seat to existing user</CardTitle>
              <CardDescription>Add a license for a user that already has an account.</CardDescription>
            </CardHeader>
            <CardContent>
              <AssignSeatForm onSuccess={fetchDashboard} />
            </CardContent>
          </Card>

          {/* Create user */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Create new user</CardTitle>
              <CardDescription>Create a user and assign a license. They will receive an email with credentials.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <CreateUserForm onSuccess={fetchDashboard} />
              <Button variant="outline" size="sm" onClick={() => setCsvModalOpen(true)}>
                Upload CSV
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* On-premise */}
        {d.isOnPremiseLicensingEnabled && d.onPremiseLicenseCode && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">On Premise License code</CardTitle>
              <CardDescription>
                Use this code in your <strong>license.txt</strong> file. Contact hello@folge.me for support.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                readOnly
                rows={6}
                className="font-mono text-sm w-full max-w-2xl"
                value={d.onPremiseLicenseCode}
              />
            </CardContent>
          </Card>
        )}

        {/* Users table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Users with assigned licenses (current and past)</CardTitle>
            <CardDescription>Assign or unassign seats and view audit dates.</CardDescription>
          </CardHeader>
          <CardContent>
            <LicenseUsersTable
              licenseUsers={licenseUsers}
              isOfflineEnabled={!!d.isOfflineLicensingEnabled}
              onAssign={(email) => handleAction('add_user', { email, send_email: 0 })}
              onUnassign={(email) => handleAction('remove_user', { email })}
              onOffline={openOfflineModal}
              onSuccess={fetchDashboard}
            />
          </CardContent>
        </Card>
      </div>

      {/* CSV Upload Modal */}
      <Dialog open={csvModalOpen} onOpenChange={setCsvModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload CSV to create users</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Upload a CSV with columns Name and Email. Passwords are generated and licenses assigned. Format: <code>Name,Email</code> (no header required).
          </p>
          <input type="file" accept=".csv" onChange={onCsvFileChange} className="block w-full text-sm" />
          {csvPreview.length > 0 && (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvPreview.map((u, i) => (
                    <TableRow key={i}>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        {csvRowStatus[i] === 'processing' && <Loader2 className="h-4 w-4 animate-spin" />}
                        {csvRowStatus[i] === 'success' && <span className="text-green-600">{csvRowMessage[i]}</span>}
                        {csvRowStatus[i] === 'error' && <span className="text-destructive">{csvRowMessage[i]}</span>}
                        {(!csvRowStatus[i] || csvRowStatus[i] === 'pending') && <span className="text-muted-foreground">Pending</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex gap-2">
                <Button onClick={createAllCsvUsers}>Create all users</Button>
                <Button variant="outline" onClick={() => setCsvModalOpen(false)}>Cancel</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Offline computers modal */}
      <Dialog open={offlineModal.open} onOpenChange={(open) => !open && setOfflineModal((p) => ({ ...p, open: false }))}>
        <DialogContent className="max-w-4xl w-[90vw] min-h-[60vh] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Offline computers
              {offlineModal.user && ` for ${offlineModal.user.fullname}, ${offlineModal.user.email}`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Add offline computers by pasting <strong>Key1</strong> from the computer; then copy <strong>Key2</strong> back into the activation dialog.
          </p>
          {offlineModal.loading ? (
            <Loader2 className="h-8 w-8 animate-spin" />
          ) : (
            <>
              {offlineModal.computers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Computer</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Key2</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {offlineModal.computers.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.machine_name}</TableCell>
                        <TableCell>{new Date(c.created_at).toLocaleString()}</TableCell>
                        <TableCell>
                          {c.key2_signed ? (
                            <Textarea
                              readOnly
                              className="font-mono text-xs min-h-[80px]"
                              value={c.key2_signed}
                              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                            />
                          ) : (
                            <div className="space-y-1">
                              <Textarea
                                readOnly
                                disabled
                                className="font-mono text-xs min-h-[80px] opacity-50"
                                value={c.key2_signed || ''}
                              />
                              <p className="text-xs text-destructive">
                                Old activation. Please update the app to 1.32.0+ from{' '}
                                <a href="https://folge.me/download" target="_blank" rel="noopener noreferrer" className="underline">
                                  folge.me/download
                                </a>{' '}
                                and regenerate the license.
                              </p>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">No offline computers for this user yet.</p>
              )}
              <div className="space-y-2 pt-4 border-t">
                <Label>Add new computer</Label>
                <Input
                  placeholder="Computer name"
                  value={offlineModal.newComputerName}
                  onChange={(e) => setOfflineModal((p) => ({ ...p, newComputerName: e.target.value }))}
                />
                <Textarea
                  placeholder="Paste Key1 from the computer"
                  value={offlineModal.key1}
                  onChange={(e) => setOfflineModal((p) => ({ ...p, key1: e.target.value }))}
                  className="min-h-[80px] font-mono text-sm"
                />
                <Button onClick={addOfflineComputer} disabled={offlineModal.adding}>
                  {offlineModal.adding ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add offline computer'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

function AssignSeatForm({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await licenseManagerAction({
        action: 'add_user',
        email: email.trim().toLowerCase(),
        send_email: sendEmail ? 1 : 0,
      });
      if ((res as { successCode?: string }).successCode) {
        toast.success(res.message);
        setEmail('');
        onSuccess();
      } else {
        toast.error(res.message || 'Failed');
      }
    } catch {
      toast.error('Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label htmlFor="assign-email">Email</Label>
        <Input
          id="assign-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          required
          className="mt-1"
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
        />
        Send email notification to user
      </label>
      <Button type="submit" disabled={submitting}>
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
      </Button>
    </form>
  );
}

function CreateUserForm({ onSuccess }: { onSuccess: () => void }) {
  const [fullname, setFullname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullname.trim() || !email.trim() || !password.trim()) return;
    setSubmitting(true);
    try {
      const res = await licenseManagerAction({
        action: 'create_user',
        email: email.trim().toLowerCase(),
        fullname: fullname.trim(),
        password: password.trim(),
      });
      if ((res as { successCode?: string }).successCode) {
        toast.success(res.message);
        setFullname('');
        setEmail('');
        setPassword('');
        onSuccess();
      } else {
        toast.error(res.message || 'Failed');
      }
    } catch {
      toast.error('Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label htmlFor="create-name">Name</Label>
        <Input id="create-name" value={fullname} onChange={(e) => setFullname(e.target.value)} required className="mt-1" />
      </div>
      <div>
        <Label htmlFor="create-email">Email</Label>
        <Input id="create-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" />
      </div>
      <div>
        <Label htmlFor="create-password">Password</Label>
        <div className="flex gap-2 mt-1">
          <Input
            id="create-password"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={3}
            className="flex-1 min-w-[280px]"
          />
          <Button
            type="button"
            variant="outline"
            size="default"
            className="shrink-0"
            onClick={() => setPassword(generateRandomPassword(15))}
            title="Generate random password (no dashes)"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Generate password
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">User will receive an automatic email with their credentials.</p>
      <Button type="submit" disabled={submitting}>
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
      </Button>
    </form>
  );
}

function LicenseUsersTable({
  licenseUsers,
  isOfflineEnabled,
  onAssign,
  onUnassign,
  onOffline,
  onSuccess,
}: {
  licenseUsers: LicenseManagerUser[];
  isOfflineEnabled: boolean;
  onAssign: (email: string) => void;
  onUnassign: (email: string) => void;
  onOffline: (userId: string) => void;
  onSuccess: () => void;
}) {
  const [removing, setRemoving] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  const handleUnassign = async (email: string) => {
    if (!window.confirm('Unassign license from this user?')) return;
    setRemoving(email);
    try {
      await licenseManagerAction({ action: 'remove_user', email });
      toast.success('License unassigned.');
      onSuccess();
    } catch {
      toast.error('Failed to unassign');
    } finally {
      setRemoving(null);
    }
  };

  const handleAssign = async (email: string) => {
    setAdding(email);
    try {
      await licenseManagerAction({ action: 'add_user', email, send_email: 0 });
      toast.success('License assigned.');
      onSuccess();
    } catch {
      toast.error('Failed to assign');
    } finally {
      setAdding(null);
    }
  };

  if (licenseUsers.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No users yet. Assign a seat to an existing user or create a new user above.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>User name</TableHead>
          <TableHead>User email</TableHead>
          <TableHead>Assigned at</TableHead>
          <TableHead>Unassigned at</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {licenseUsers.map((entry) => (
          <TableRow key={`${entry.user_id}-${entry.assigned_at || ''}`}>
            <TableCell>
              <span className="font-medium">{entry.fullname}</span>
              {entry.is_admin && (
                <Badge variant="secondary" className="ml-2 text-xs">Admin</Badge>
              )}
            </TableCell>
            <TableCell>{entry.email}</TableCell>
            <TableCell className="text-muted-foreground">
              {entry.assigned_at ? new Date(entry.assigned_at).toLocaleString() : '—'}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {entry.unassigned_at ? new Date(entry.unassigned_at).toLocaleString() : '—'}
            </TableCell>
            <TableCell>
              <Badge variant={entry.status === 'current' ? 'default' : 'secondary'}>
                {entry.status === 'current' ? 'Assigned' : 'Unassigned'}
              </Badge>
            </TableCell>
            <TableCell>
              {entry.status === 'current' ? (
                <>
                  {(!entry.is_admin || !entry.without_license) && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mr-2"
                        disabled={removing === entry.email}
                        onClick={() => handleUnassign(entry.email)}
                      >
                        {removing === entry.email ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Unassign'}
                      </Button>
                      {isOfflineEnabled && entry.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onOffline(String(entry.id))}
                        >
                          Offline
                        </Button>
                      )}
                    </>
                  )}
                </>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  disabled={adding === entry.email}
                  onClick={() => handleAssign(entry.email)}
                >
                  {adding === entry.email ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assign'}
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default LicenseManagerPage;
